import type { ComponentProps } from 'react'
import type { components } from '@joyful/openapi/types.gen'
import type { BadgeProps } from '../../core'
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Ellipsis,
  QueryState,
  RelativeTimestamp,
  RotatingChevron,
  Separator,
  TextLink,
} from '../../core'
import type { EnumValueMeta } from '@joyful/utils'
import { cn, createEnumSchema, tv } from '@joyful/utils'
import { GitCommitHorizontalIcon } from 'lucide-react'

import { useApiQuery } from '../utils/reactQuery'
interface DeploymentColorMeta extends EnumValueMeta {
  badgeVariant: BadgeProps['variant']
}

const DeploymentColorSchema = createEnumSchema<
  components['schemas']['DeploymentColor'],
  DeploymentColorMeta
>([
  { badgeVariant: 'info', title: 'Blue', value: 'blue' },
  { badgeVariant: 'success', title: 'Green', value: 'green' },
])

export interface DeploymentInfoProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  frontendCommitMessage?: string | null
  frontendCommitSha?: string | null
  frontendCommitTimestamp?: string | null
}

export function DeploymentInfo({
  className,
  frontendCommitMessage = null,
  frontendCommitSha = null,
  frontendCommitTimestamp = null,
  ...props
}: DeploymentInfoProps) {
  const deploymentInfoQuery = useApiQuery('get', '/deployment/info')

  return (
    <div className={cn('text-tremor-content text-xs', className)} {...props}>
      <QueryState query={deploymentInfoQuery}>
        {deploymentInfoQuery.isSuccess ? (
          <>
            <section className="flex flex-col gap-y-2 px-2 py-2">
              <h4 className="text-tremor-content-emphasis text-xs font-semibold">
                {`API`}
              </h4>

              <div className="flex flex-col gap-y-2">
                <CommitRow
                  color={deploymentInfoQuery.data.active_color}
                  activity="active"
                  commitMessage={deploymentInfoQuery.data.commit_message}
                  commitSha={deploymentInfoQuery.data.commit_sha}
                  deploymentTimestamp={deploymentInfoQuery.data.deployment_time}
                />

                {deploymentInfoQuery.data.other_environment ? (
                  <CommitRow
                    color={deploymentInfoQuery.data.other_environment.color}
                    activity="inactive"
                    commitMessage={
                      deploymentInfoQuery.data.other_environment.commit_message
                    }
                    commitSha={
                      deploymentInfoQuery.data.other_environment.commit_sha
                    }
                    deploymentTimestamp={
                      deploymentInfoQuery.data.other_environment.deployment_time
                    }
                  />
                ) : null}
              </div>
            </section>

            <Separator />

            <section className="flex flex-col gap-y-2 px-2 py-2">
              <h4 className="text-tremor-content-emphasis text-xs font-semibold">
                {`Frontend`}
              </h4>

              <CommitRow
                commitMessage={frontendCommitMessage}
                commitSha={frontendCommitSha}
                deploymentTimestamp={frontendCommitTimestamp}
              />
            </section>
          </>
        ) : null}
      </QueryState>
    </div>
  )
}

const commitRowVariants = tv({
  defaultVariants: {
    activity: 'active',
  },

  slots: {
    chevronColor: null,
    commitMessage: null,
    dot: 'inline-block size-2 shrink-0 rounded-full',
    label: 'text-xs',
    sha: 'font-mono text-xs',
    trigger:
      'flex cursor-pointer items-center justify-between gap-x-2 rounded-sm px-2 -mx-1 py-1 hover:bg-tremor-background-subtle data-[state=open]:bg-tremor-background-subtle transition-colors',
  },

  variants: {
    activity: {
      active: {
        chevronColor: 'text-tremor-content',
        commitMessage: 'text-tremor-content-emphasis',
        dot: 'bg-emerald-500',
        label: 'text-tremor-content-emphasis font-medium',
        sha: 'text-tremor-content-emphasis',
      },
      inactive: {
        chevronColor: 'text-tremor-content-subtle',
        commitMessage: 'text-tremor-content-subtle',
        dot: 'border-tremor-content-subtle border bg-transparent',
        label: 'text-tremor-content-subtle',
        sha: 'text-tremor-content-subtle',
      },
    },
  },
})

interface CommitRowProps {
  /**
   * @default 'active'
   */
  activity?: 'active' | 'inactive'
  color?: components['schemas']['DeploymentColor'] | null
  commitMessage: string | null
  commitSha: string | null
  deploymentTimestamp: string | null
}

function CommitRow({
  activity = 'active',
  color,
  commitMessage,
  commitSha,
  deploymentTimestamp,
}: CommitRowProps) {
  const styles = commitRowVariants({ activity })
  const colorMeta = color ? DeploymentColorSchema.meta(color) : null

  return (
    <Collapsible
      className="group flex flex-col"
      defaultOpen={activity === 'active'}
    >
      <CollapsibleTrigger className={styles.trigger()}>
        <div className="flex items-center gap-x-1.5">
          <span className={styles.dot()} />
          <span className={styles.label()}>
            {activity === 'active' ? `Active` : `Inactive`}
          </span>
          {colorMeta ? (
            <Badge size="xxs" variant={colorMeta.badgeVariant}>
              {colorMeta.title}
            </Badge>
          ) : null}
        </div>

        <div className="flex items-center gap-x-1.5">
          {deploymentTimestamp ? (
            <RelativeTimestamp
              timestamp={deploymentTimestamp}
              tone={activity === 'inactive' ? 'subtle' : 'default'}
            />
          ) : null}
          <RotatingChevron
            orientation="horizontal"
            className={styles.chevronColor()}
          />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="pt-1 pl-3.5">
          <Ellipsis numLines={2}>
            <GitCommitHorizontalIcon className="text-tremor-content mr-1 inline-block size-3.5 align-middle" />

            {commitSha ? (
              <span className="mr-1 align-middle">
                <TextLink
                  to={`https://github.com/Joyful-Health/private-practice-os/commit/${commitSha}`}
                >
                  <code className={styles.sha()}>
                    {commitSha.substring(0, 7)}
                  </code>
                </TextLink>
              </span>
            ) : null}

            {commitMessage ? (
              <span className={styles.commitMessage()}>{commitMessage}</span>
            ) : null}
          </Ellipsis>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
